// src/cabrillo-analizador.js — Analizador Cabrillo, arquitectura nativa.
//
// log CM → cabrillo-logs.js → parser-cabrillo.js → modelo.js → motor.js →
// render nativo (src/cabrillo/analizador.js + src/cabrillo/render/*.js).
// Sin iframe, sin cabrillo-widget.js, sin contentWindow.load().
//
// Ver src/cabrillo/CREDITOS.md (autoría e historia de la integración) y
// docs/integrations/analizador-cabrillo.md (arquitectura).

import { inicializarIdioma, t } from './i18n.js';
import { auth } from './firebase.js';
import { onAuthStateChanged } from 'firebase/auth';

import { listarConcursosConLog, etiquetaConcurso, obtenerTextoLog } from './cabrillo-logs.js';
import { parse } from './cabrillo/parser-cabrillo.js';
import { construirModelo } from './cabrillo/modelo.js';
import { crearAnalizador } from './cabrillo/motor.js';
import { montarAnalizador } from './cabrillo/analizador.js';
import { CT, ST } from './cabrillo/datos.js';

inicializarIdioma();

// Tema: aplicar preferencia guardada desde la app principal.
(function initTema() {
    const saved = localStorage.getItem('theme');
    document.body.classList.add(saved === 'light' ? 'tema-claro' : 'tema-oscuro');
})();

// ============================================================
// Referencias DOM del analizador nativo (cn-*)
// ============================================================

const raizAnalizador = document.getElementById('cabrillo-nativo-root');
const estadoInicialEl = document.getElementById('cabrillo-estado-inicial');

const elsAnalizador = {
    titulo: document.getElementById('cn-title'),
    sub: document.getElementById('cn-sub'),
    play: document.getElementById('cn-play'),
    all: document.getElementById('cn-all'),
    spd: document.getElementById('cn-spd'),
    clock: document.getElementById('cn-clock'),
    sl: document.getElementById('cn-sl'),
    bf: document.getElementById('cn-bf'),
    mini: document.getElementById('cn-mini'),
    cv: document.getElementById('cn-cv'),
    leg: document.getElementById('cn-leg'),
    mp: document.getElementById('cn-mp'),
    tt: document.getElementById('cabrillo-nativo-tt'),
    md: document.getElementById('cn-md'),
    ml: document.getElementById('cn-ml'),
    rt: document.getElementById('cn-rt'),
    kp: document.getElementById('cn-kp'),
    mx: document.getElementById('cn-mx'),
    tc: document.getElementById('cn-tc'),
    tz: document.getElementById('cn-tz'),
};

let motorActual = null;
let montajeActual = null;

// ============================================================
// Selector de concurso — logs ya almacenados en Contest Manager
// ============================================================

const selectEl = document.getElementById('cabrillo-select-concurso');
const btnAnalizar = document.getElementById('cabrillo-btn-analizar');
const estadoEl = document.getElementById('cabrillo-selector-estado');

let listaConcursos = [];

function setEstado(texto, esError = false) {
    if (!estadoEl) return;
    estadoEl.textContent = texto || '';
    estadoEl.classList.toggle('cabrillo-selector-estado--error', Boolean(esError));
}

function poblarSelectMensaje(texto) {
    if (!selectEl) return;
    selectEl.innerHTML = '';
    const opt = document.createElement('option');
    opt.value = '';
    opt.textContent = texto;
    selectEl.appendChild(opt);
    selectEl.disabled = true;
    if (btnAnalizar) btnAnalizar.disabled = true;
}

// Sin sesión: enlace al login existente de la app (/app). Tras autenticarse,
// src/auth.js devuelve al usuario a esta página vía el parámetro `next`.
function mostrarEnlaceLogin() {
    if (!estadoEl) return;
    estadoEl.textContent = '';
    estadoEl.classList.remove('cabrillo-selector-estado--error');
    const enlace = document.createElement('a');
    enlace.href = '/app?next=' + encodeURIComponent('/analizador-cabrillo');
    enlace.className = 'btn-guardar';
    enlace.style.cssText = 'display:inline-block;text-decoration:none;';
    enlace.textContent = t('cabrillo.selector.no-auth.login');
    estadoEl.appendChild(enlace);
}

async function inicializarSelector(uid) {
    setEstado('');
    poblarSelectMensaje(t('cabrillo.selector.cargando'));

    try {
        listaConcursos = await listarConcursosConLog(uid);
    } catch (error) {
        console.error('[CabrilloAnalizador] Error listando concursos:', error);
        listaConcursos = [];
    }

    if (!listaConcursos.length) {
        poblarSelectMensaje(t('cabrillo.selector.vacio'));
        return;
    }

    selectEl.innerHTML = '';
    listaConcursos.forEach((item, i) => {
        const opt = document.createElement('option');
        opt.value = String(i);
        opt.textContent = etiquetaConcurso(item.edicion);
        selectEl.appendChild(opt);
    });
    selectEl.disabled = false;
    btnAnalizar.disabled = false;
}

onAuthStateChanged(auth, (user) => {
    if (user) {
        inicializarSelector(user.uid);
    } else {
        listaConcursos = [];
        poblarSelectMensaje(t('cabrillo.selector.no-auth'));
        mostrarEnlaceLogin();
    }
});

if (btnAnalizar) {
    btnAnalizar.addEventListener('click', async () => {
        const item = listaConcursos[Number(selectEl.value)];
        if (!item) return;

        const textoBtnOriginal = btnAnalizar.textContent;
        btnAnalizar.disabled = true;
        selectEl.disabled = true;
        btnAnalizar.textContent = t('cabrillo.selector.btn.cargando');
        setEstado(t('cabrillo.estado.cargando-log'));

        try {
            const texto = await obtenerTextoLog(item.logFile);
            const resultado = parse(texto, CT, ST);

            if (resultado.err) {
                setEstado(resultado.err, true);
                return;
            }

            // Reemplazo de un análisis anterior por uno nuevo: se pausa y
            // se desmonta el motor/listeners previos antes de crear los
            // nuevos, para no acumular setInterval ni listeners globales
            // duplicados entre selecciones sucesivas.
            motorActual?.pausar();
            montajeActual?.destruir();

            // Se revela el contenedor ANTES de montar el render: con
            // `hidden` todavía puesto, #cabrillo-nativo-root mide 0x0
            // (display:none colapsa el layout de todos sus descendientes),
            // así que cualquier canvas.clientWidth leído durante el primer
            // draw() daría 0 y el canvas quedaría vacío hasta el próximo
            // resize real. Por eso se revela primero y se monta después.
            estadoInicialEl.style.display = 'none';
            raizAnalizador.hidden = false;

            const modelo = construirModelo(resultado);
            motorActual = crearAnalizador(modelo);
            montajeActual = montarAnalizador(elsAnalizador, motorActual);

            setEstado('');
        } catch (error) {
            console.error('[CabrilloAnalizador] Error cargando log:', error);
            setEstado(error?.message || t('cabrillo.estado.error'), true);
        } finally {
            btnAnalizar.disabled = false;
            selectEl.disabled = false;
            btnAnalizar.textContent = textoBtnOriginal;
        }
    });
}
